import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel, GridFilterModel, GridFilterItem, getGridStringOperators } from '@mui/x-data-grid';
import { Power, FileText } from 'lucide-react';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import styles from './userTabs.module.scss';
import { userService } from 'components/api/users';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import CustomPagination from 'components/common/pagination/pagination';
import { LiveLog } from '../../Games/LiveLog';
import { SessionRounds } from '../../Games/SessionRounds';

interface UserGameHistoryProps {
  userId: string;
  siteId: string | number;
}

interface GameHistoryRow {
  id: string;
  gameName: string;
  bet: number;
  win: number;
  date: string;
  isActive: boolean;
  gameImageUrl?: string;
}

const UserGameHistory: React.FC<UserGameHistoryProps> = ({ userId, siteId }) => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState<string>('id');
  const [sortValue, setSortValue] = useState<'ASC' | 'DESC'>('DESC');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [filters, setFilters] = useState<Record<string, any>>({});

  const [selectedSessionForLog, setSelectedSessionForLog] = useState<any>(null);

  const customStringOperators = getGridStringOperators().filter(
    (operator) => operator.value === 'contains',
  );

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userService.getUserSessions(userId, {
        page,
        limit,
        sortField,
        sortValue,
        filters: '',
        ...filters,
      });

      if (response?.data?.docs) {
        setData(response.data.docs);
        setTotal(response.data.totalDocs || 0);
      }
    } catch (error: any) {
      console.error('Failed to fetch user game history:', error);
      dispatch(showToast({ text: error.response?.data?.message || 'Failed to fetch history', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [userId, page, limit, sortField, sortValue, filters, dispatch]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field);
      setSortValue(model[0].sort?.toUpperCase() as 'ASC' | 'DESC');
    } else {
      setSortField('id');
      setSortValue('DESC');
    }
  }, []);

  const handleFilterModelChange = useCallback((model: GridFilterModel) => {
    setFilterModel(model);
    const newFilters: Record<string, any> = {};
    model.items.forEach((item: GridFilterItem) => {
      if (item.field && item.value !== undefined && item.value !== '') {
        newFilters[item.field] = item.value;
      }
    });
    setFilters(newFilters);
    setPage(1);
  }, []);

  const handleLiveLogClick = (row: any) => {
    setSelectedSessionForLog(row);
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90, sortable: true, filterOperators: customStringOperators },
    { 
      field: 'gameTitle', 
      headerName: 'Game', 
      flex: 1, 
      minWidth: 200,
      sortable: true,
      filterOperators: customStringOperators,
      headerClassName: 'align-left-header',
      cellClassName: 'align-left-cell',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', justifyContent: 'flex-start' }}>
          {params.row.gameImageUrl && (
            <Box 
              component="img"
              src={params.row.gameImageUrl}
              alt={params.value}
              sx={{ width: 40, height: 28, borderRadius: 1, objectFit: 'cover' }}
            />
          )}
          <Typography noWrap variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'gameId', 
      headerName: 'Game ID', 
      width: 110,
      filterOperators: customStringOperators,
    },
    { 
      field: 'ip', 
      headerName: 'IP Address', 
      width: 150,
      filterOperators: customStringOperators,
    },
    { 
      field: 'createdAt', 
      headerName: 'Date', 
      width: 180,
      filterOperators: customStringOperators,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleString() : '-'
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isActive = params.row.status === 'active' || params.row.isActive;
        return (
          <Button
            size="small"
            variant="contained"
            startIcon={isActive ? <Power size={14} /> : <FileText size={14} />}
            sx={{
              width: 110,
              bgcolor: isActive ? '#f92672' : 'rgba(255,255,255,0.05)',
              color: isActive ? 'white' : '#8892b0',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: isActive ? 'rgba(249,38,114,0.8)' : 'rgba(255,255,255,0.1)',
                boxShadow: 'none',
              }
            }}
            onClick={() => handleLiveLogClick(params.row)}
          >
            {isActive ? 'Active log' : 'Logs'}
          </Button>
        );
      }
    }
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={page - 1}
      onPageChange={(_, newPage) => setPage(newPage + 1)}
      rowsPerPage={limit}
      onRowsPerPageChange={(e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
      }}
    />
  );

  if (selectedSessionForLog) {
    const isActive = selectedSessionForLog.status === 'active' || selectedSessionForLog.isActive;
    
    if (isActive) {
      return (
        <LiveLog
          session={selectedSessionForLog}
          siteId={siteId}
          onBack={() => {
            setSelectedSessionForLog(null);
            fetchHistory();
          }}
        />
      );
    } else {
      return (
        <SessionRounds
          userId={userId}
          session={selectedSessionForLog}
          onBack={() => {
            setSelectedSessionForLog(null);
            fetchHistory();
          }}
        />
      );
    }
  }

  return (
    <Box className={styles.tabContent}>
      <Typography variant="h6" className={styles.title} sx={{ mb: 2 }}>
        Game History
      </Typography>
      
      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data}
          rowCount={total}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          filterModel={filterModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          sx={{
            ...commonGridStyles,
            '& .align-left-header .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start !important',
            },
            '& .align-left-cell': {
              justifyContent: 'flex-start !important',
            },
          }}
          disableRowSelectionOnClick
          autoHeight
          hideFooterPagination
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, color: 'var(--text-secondary)' }}>
                No game history found
              </Box>
            ),
          }}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default UserGameHistory;
